import {
  Button,
  ConfigProvider,
  Flex,
  Space,
  Table,
  type TableRef,
  TableRowMenuCell,
  Text,
  useTableRowSelection,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { formatBidRate } from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import { PREVIEW_ROW_ID } from '@/features/project/_utils/constants';
import {
  getContractAmountOrExpendituresTitle,
  getContractName,
  paymentNumberConverter,
  paymentValueConverter,
} from '@/features/project/_utils/payment';
import { isBefore2025 } from '@/features/project/_utils/project';
import type { PaymentType } from '@/features/project/projects/_models/payment';
import type {
  PaymentContractModel,
  PaymentContractsModel,
} from '@/features/project/projects/_models/payment-contract';
import { useId } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { TABLE_MONETARY_AMOUNT_WIDTH, TABLE_ROW_MENU_WIDTH } from '@/utils';
import classNames from 'classnames';
import { type FC, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { usePreviewRowState } from '../../income-and-expenditure/_components/shared/PreviewRowStateProvider';
import styles from './PaymentContractsTable.module.scss';

type Props = {
  data: z.infer<typeof PaymentContractsModel>;
  paymentGroupId: string;
  paymentId: string;
  paymentType: PaymentType;
  onClickDelete: (value: z.infer<typeof PaymentContractModel>) => void;
};

const tableStyles = classNames(styles.ant_table_content);

const MINIMUM_ROW_NUM_TO_SCROLL = 5;
const PREVIEW_ROW: z.infer<typeof PaymentContractModel> = {
  id: PREVIEW_ROW_ID,
  overview: '',
  isOthers: false,
  amount: 0,
  contractMethod: null,
  contractMethodDescription: null,
  numberOfApplicants: null,
  bidRate: null,
  reason: null,
  amountBreakdown: [],
  paymentId: '',
  hideBidRate: false,
};

export const PaymentContractsTable: FC<Props> = ({
  data,
  paymentGroupId,
  paymentId,
  paymentType,
  onClickDelete,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const tableRef = useRef<TableRef>(null);
  const { setDrawerState } = useProjectDetailDrawerState();
  const { previewRowState, setPreviewRowState } = usePreviewRowState();
  const { selectedRowKeys, setSelectedRowKeys } = useTableRowSelection();
  const { id: projectId } = useId();
  const project = useProject(projectId);
  const editPermission = useProjectPermission(true);

  const showPreviewRow = useMemo(
    () =>
      previewRowState?.type === 'payment-contract' &&
      previewRowState.paymentGroupId === paymentGroupId &&
      previewRowState.paymentId === paymentId &&
      previewRowState.paymentType === paymentType,
    [previewRowState, paymentGroupId, paymentId, paymentType],
  );

  const tableData = showPreviewRow ? [...data, PREVIEW_ROW] : data;

  const isKsStandardAfter2025 = useMemo(
    () =>
      project.data?.sheetType === 'KS' &&
      project.data?.fundAdditionalAttributes?.fundSheetType === 'standard' &&
      !isBefore2025(project.data),
    [project.data],
  );

  useEffect(() => {
    if (showPreviewRow && data.length > MINIMUM_ROW_NUM_TO_SCROLL) {
      tableRef.current?.scrollTo({
        index: data.length - MINIMUM_ROW_NUM_TO_SCROLL,
      });
    }
  }, [showPreviewRow, data]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: '#FAFAFA',
            headerColor: '#00000073',
            rowExpandedBg: '#FAFAFA',
            headerSplitColor: '#A0A0A0',
          },
        },
      }}
    >
      <Table
        enableSticky={false}
        ref={tableRef}
        rowKey="id"
        className={tableStyles}
        dataSource={tableData}
        locale={{ emptyText: ' ' }}
        style={{ minWidth: 960 }}
        rowSelection={{
          renderCell: () => null,
          hideSelectAll: true,
          columnWidth: 0,
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        columns={[
          {
            title: (
              <Text
                variant="footnote"
                weight="bold"
                color="secondary"
                text={`${t('payment-contract-overview')}/${t(
                  'contract-method',
                )}`}
              />
            ),
            key: 'overview',
            dataIndex: 'overview',
            width: 370,
            render: (_, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return (
                  <Text
                    weight="regular"
                    color="secondary"
                    text={t('preview-row-description', {
                      item: t('payment-contract'),
                    })}
                  />
                );
              }
              return (
                <Space direction="vertical" size="small">
                  <Text weight="regular" text={record.overview} />
                  <Text
                    variant="footnote"
                    weight="regular"
                    text={getContractName(
                      record.contractMethod,
                      record.contractMethodDescription,
                    )}
                  />
                </Space>
              );
            },
          },
          {
            title: (
              <Text
                variant="footnote"
                color="secondary"
                text={getContractAmountOrExpendituresTitle(paymentType)}
              />
            ),
            key: 'amount',
            dataIndex: 'amount',
            align: 'right',
            width: TABLE_MONETARY_AMOUNT_WIDTH,
            render: (value, record) => {
              if (record.id === PREVIEW_ROW_ID) {
                return null;
              }
              return (
                <Text
                  weight="regular"
                  text={paymentValueConverter(value, unit, false, undefined)}
                />
              );
            },
          },
          ...(!isKsStandardAfter2025
            ? [
                {
                  title: (
                    <Text
                      variant="footnote"
                      color="secondary"
                      text={t('number-of-applicants-title')}
                    />
                  ),
                  key: 'numberOfApplicants',
                  dataIndex: 'numberOfApplicants',
                  align: 'right' as const,
                  width: 80,
                  render: (value, record) => {
                    if (record.id === PREVIEW_ROW_ID) {
                      return null;
                    }
                    return (
                      <Text
                        weight="regular"
                        text={paymentNumberConverter(value)}
                      />
                    );
                  },
                },
                {
                  title: (
                    <Text
                      variant="footnote"
                      color="secondary"
                      text={t('bid-rate')}
                    />
                  ),
                  dataIndex: 'bidRate',
                  align: 'right' as const,
                  width: 80,
                  render: (value, record) => {
                    if (record.id === PREVIEW_ROW_ID) {
                      return null;
                    }
                    return (
                      <Text weight="regular" text={formatBidRate(value)} />
                    );
                  },
                },
                {
                  title: (
                    <Text
                      variant="footnote"
                      color="secondary"
                      text={t('payment-reason-title')}
                    />
                  ),
                  key: 'reason',
                  dataIndex: 'reason',

                  render: (value, record) => {
                    if (record.id === PREVIEW_ROW_ID) {
                      return null;
                    }
                    return <Text weight="regular" text={value} maxRows={3} />;
                  },
                },
              ]
            : // Note: 空白を作るために空オブジェクトを挿入
              [{}]),
          ...(project.data && !project.data.isLocked && editPermission.data
            ? [
                {
                  title: (
                    <PublishedProjectMutableGuard>
                      <Flex justify="end">
                        <Button
                          onClick={() => {
                            setDrawerState({
                              open: true,
                              type: 'payment-contract',
                              isEditing: false,
                              paymentGroupId,
                              paymentId,
                              paymentType,
                            });
                            setPreviewRowState({
                              type: 'payment-contract',
                              paymentGroupId,
                              paymentId,
                              paymentType,
                            });
                            setSelectedRowKeys([PREVIEW_ROW_ID]);
                          }}
                        >
                          {t('create-payment-contract')}
                        </Button>
                      </Flex>
                    </PublishedProjectMutableGuard>
                  ),
                  key: 'actions',
                  width: Number(TABLE_ROW_MENU_WIDTH) + 20,
                  fixed: 'right' as const,
                  render: (_, record: z.infer<typeof PaymentContractModel>) => (
                    <PublishedProjectMutableGuard>
                      <Flex justify="end" style={{ marginRight: 8 }}>
                        <TableRowMenuCell
                          onEdit={() => {
                            setDrawerState({
                              open: true,
                              type: 'payment-contract',
                              isEditing: true,
                              paymentGroupId,
                              paymentId,
                              paymentContractId: record.id,
                              paymentType,
                            });
                          }}
                          items={[
                            {
                              key: 'delete',
                              label: t('delete'),
                              danger: true,
                              onClick: () => {
                                onClickDelete(record);
                              },
                            },
                          ]}
                        />
                      </Flex>
                    </PublishedProjectMutableGuard>
                  ),
                },
              ]
            : []),
        ]}
        pagination={false}
      />
    </ConfigProvider>
  );
};
